import { Box } from "@mui/material";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useGetMe, useGetStores } from "@/api";
import { useCommission } from "@/hooks/useCommission";
import { getRoleDefinition, resolveRoleKeyFromRoleName } from "@/lib/settings/access";
import { useAuthStore } from "@/stores/auth.store";
import { useCommissionGateStore } from "@/stores/commissionGate.store";
import { useSessionStore } from "@/stores/session.store";
import { AppNavbar } from "../AppNavbar";
import { MobileBottomNav } from "../MobileBottomNav";
import { CommissionBanner, type CommissionBannerView } from "../CommissionBanner";
import { CommissionDebtModal } from "../CommissionDebtModal";
import type { GuardLayoutProps } from "./GuardLayout.types";
import * as styles from "./GuardLayout.styles";

export type { GuardLayoutProps } from "./GuardLayout.types";

export function GuardLayout({ children }: GuardLayoutProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const setActiveStoreId = useSessionStore((state) => state.setActiveStoreId);
  const activePath = useRouterState({ select: (s) => s.location.pathname });
  const commission = useCommission();
  const debtModal = useCommissionGateStore((state) => state.modal);
  const closeDebtModal = useCommissionGateStore((state) => state.closeModal);

  const commissionBanner = useMemo<CommissionBannerView | null>(() => {
    if (!commission.hasShop) return null;
    if (commission.isRestricted) {
      return {
        variant: "restricted",
        tone: "error",
        outstandingDebt: commission.outstandingDebt,
        debtLimit: commission.debtLimit,
        canPayDebt: commission.canPayDebt,
      };
    }
    if (!commission.onlinePaymentEnabled) return { variant: "noIban", tone: "warning" };
    return null;
  }, [commission]);

  const { data: storesData, isSuccess: isStoresSuccess } = useGetStores(undefined, {
    query: { enabled: isAuthenticated },
  });

  const { data: meData } = useGetMe({
    query: { enabled: isAuthenticated },
  });

  const stores = useMemo(() => storesData?.data?.data ?? [], [storesData]);
  const userName = meData?.data?.data?.full_name ?? meData?.data?.data?.username;

  const { t } = useTranslation("settings");
  // Persian role label per store ("مالک فروشگاه"), not the backend's role key ("store_owner").
  const storeRoles = useMemo(() => {
    const meStores = meData?.data?.data?.stores ?? [];
    return Object.fromEntries(
      meStores.map((store) => {
        const roleKey = resolveRoleKeyFromRoleName(store.role_name);
        return [store.id, roleKey ? t(getRoleDefinition(roleKey).labelKey) : ""];
      }),
    );
  }, [meData, t]);

  const userRole = useMemo(() => {
    if (activeStoreId === null) return undefined;
    return storeRoles[activeStoreId];
  }, [activeStoreId, storeRoles]);

  useEffect(() => {
    if (!isStoresSuccess || stores.length === 0) return;

    if (activeStoreId !== null) {
      const exists = stores.some((store) => store.id === activeStoreId);
      if (!exists) setActiveStoreId(stores[0].id);
      return;
    }

    setActiveStoreId(stores[0].id);
  }, [isStoresSuccess, stores, activeStoreId, setActiveStoreId]);

  const activeStoreName = useMemo(() => {
    if (activeStoreId === null) return undefined;
    return stores.find((store) => store.id === activeStoreId)?.name;
  }, [stores, activeStoreId]);

  return (
    <Box sx={styles.root}>
      <AppNavbar
        storeName={activeStoreName}
        userName={userName}
        userRole={userRole}
        storeRoles={storeRoles}
        stores={stores}
        activeStoreId={activeStoreId}
        onSelectStore={setActiveStoreId}
        activePath={activePath}
        notificationCount={0}
      />
      {commissionBanner ? <CommissionBanner view={commissionBanner} /> : null}
      <Box component="main" sx={styles.main}>
        {children}
      </Box>
      <MobileBottomNav activePath={activePath} />

      <CommissionDebtModal
        open={debtModal !== null}
        canPayDebt={commission.canPayDebt}
        messageFa={debtModal?.messageFa}
        outstandingDebt={debtModal?.outstandingDebt}
        onClose={closeDebtModal}
      />
    </Box>
  );
}
